import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useBlocker } from 'react-router-dom';
import cl from 'classnames';
import { BlockerFunction } from '@remix-run/router/router';
import { Account, BillingRecord } from 'src/models';
import { RoleEnum } from 'src/security/roles';
import { SALES, HOTELS, REPORTS } from 'src/constants';
import {
  usePage,
  useContextData,
  useXlsxWriter,
  XlsxTableConfig,
  useLanguage,
} from 'src/hooks';
import { compareString, currencySign, formatDate, uuidV4 } from 'src/helper';
import { useAppDispatch } from 'src/store';
import { useAccount, useAccountCompanyList } from 'src/services/auth';
import {
  useBilling,
  useBillingState,
  fetchBillingAction,
  fetchBillingForExportAction,
} from 'src/services/billing';
import { resetBillingState } from 'src/store/reducers/billing';
import {
  PageSwitcher,
  StickyTable,
  StickyTableConfig,
  useStickyCols,
  ExportSelect,
  ExportModal,
  ExportMode,
  getExportDates,
} from 'src/components';
import {
  FilterModal,
  FilterModalButton,
  FilterModalIndicator,
  useSearchInput,
  useDateRangePicker,
  Link,
} from 'src/components/general';
import { ReactComponent as Crown } from 'src/icons/crown.svg';
import styles from './reportsSalesStyle.module.scss';

export const ReportsSales = () => {
  const {
    header: { width: headerWidth },
    navigator: { setBackUrl },
  } = useContextData();
  const dispatch = useAppDispatch();
  const state = useBillingState();
  const billingRecords = useBilling();
  const account = useAccount() as Account;
  const companyList = useAccountCompanyList();
  const { writeToXlsx } = useXlsxWriter<BillingRecord>();
  const { t, currentLang } = useLanguage();
  const {
    access: { accessMap },
  } = usePage();
  const [isLoading, setIsLoading] = useState(state.isPending);
  const [isFiltered, setIsFiltered] = useState(false);

  const [exportData, setExportData] = useState<{
    uuid: string;
    filename: string;
    records: BillingRecord[];
  } | null>(null);
  const [exportReqUuid, setExportReqUuid] = useState<string | null>(null);

  const fetchExportBilling = useCallback(
    (mode: ExportMode) => {
      const dates = getExportDates(mode);
      const uuid = uuidV4();
      setExportReqUuid(uuid);

      dispatch(
        fetchBillingForExportAction({
          params: {
            start: dates[0].format,
            end: dates[1].format,
          },
        }),
      ).then((res) => {
        if (fetchBillingForExportAction.fulfilled.match(res)) {
          setExportData({
            uuid,
            filename: `reports_sales_${dates[0].localString}_-_${dates[1].localString}.xlsx`,
            records: res.payload.map((record) => ({
              ...record,
              conversion: record.sessions
                ? record.totalSucceededCount / record.sessions
                : 0,
              headsetsWithoutSessions: record.headsetsWithoutSessions ?? 0,
            })),
          });
        }

        if (fetchBillingForExportAction.rejected.match(res)) {
          setExportReqUuid(null);
        }
      });
    },
    [dispatch],
  );

  const {
    range,
    initRange,
    dateRangePicker,
    modalDateRangePicker,
    applyDRPChanges,
    resetDRPChanges,
    resetDRPToInit,
    clearDRP,
  } = useDateRangePicker({
    inputClassName: styles.datepickerInput,
    modalInputClassName: styles.datepickerModalInput,
    disabled: isLoading,
  });

  const xlsxFileName = useMemo(() => {
    const rangePart =
      range.start && range.end
        ? `_${range.start.localString}_-_${range.end.localString}`
        : '';

    return `reports_sales${rangePart}.xlsx`;
  }, [range]);

  const rangeIndicatorText = useMemo(
    () =>
      currentLang && range.start && range.end ? (
        <>
          <span className={styles.nowrap}>{range.start.localString}</span>
          {' - '}
          <span className={styles.nowrap}>{range.end.localString}</span>
        </>
      ) : undefined,
    [range.end, range.start, currentLang],
  );

  const [showSearchModal, setShowSearchModal] = useState(false);
  const [showFiltersModal, setShowFiltersModal] = useState(false);

  const {
    searchString,
    searchInput,
    modalSearchInput,
    applySearch,
    cancelSearch,
    clearSearch,
    focusInput,
  } = useSearchInput({
    inputClassName: styles.searchInput,
    modalInputClassName: styles.modalSearchInput,
    disabled: isLoading,
    onEnter: () => {
      setShowSearchModal(false);
    },
  });

  useEffect(() => {
    if (showSearchModal) {
      focusInput();
    }
  }, [showSearchModal, focusInput]);

  const getCompanyCommission = useCallback(
    (companyId: string): number =>
      companyList.find((c) => c.id === companyId)?.commission || 0,
    [companyList],
  );

  const xlsxConfig: XlsxTableConfig<BillingRecord>[] = useMemo(
    () => [
      {
        title: t('page.reports.sales.company'),
        field: 'name',
        key: 'company',
      },
      ...(account?.role === RoleEnum.airpano
        ? ([
            {
              title: t('page.reports.sales.headsets'),
              key: 'headsets',
              field: 'headsets',
            },
            {
              title: t('page.reports.sales.headsetsWithoutSessions'),
              field: 'headsetsWithoutSessions',
              key: 'headsetsWithoutSessions',
            },
            {
              title: t('page.reports.sales.conversion'),
              key: 'conversion',
              field: 'conversion',
              render: (value) => ((value ?? 0) as number).toFixed(2),
            },
            {
              title: t('page.reports.sales.sessions'),
              key: 'sessions',
              field: 'sessions',
            },
            {
              title: t('page.reports.sales.paymentsCount'),
              field: 'totalSucceededCount',
              key: 'paymentsCount',
            },
          ] as XlsxTableConfig<BillingRecord>[])
        : ([
            {
              title: t('page.reports.sales.connectionDate'),
              field: 'activatedAt',
              key: 'connectionDate',
              render: (value) =>
                value ? formatDate(value) : t('general.inactive'),
              summaryRender: () => ' ',
            },
            {
              title: t('page.reports.sales.hotelCommission'),
              field: 'companyId',
              key: 'hotelCommission',
              render: (value) => `${getCompanyCommission(value)} %`,
              summaryRender: () => ' ',
            },
            {
              title: t('page.reports.sales.paymentsCount'),
              field: 'totalSucceededCount',
              key: 'paymentsCount',
            },
            {
              title: t('page.reports.sales.sessions'),
              key: 'sessions',
              field: 'sessions',
            },
            {
              title: t('page.reports.sales.conversion'),
              key: 'conversion',
              field: 'conversion',
              render: (value) => ((value ?? 0) as number).toFixed(2),
            },
            {
              title: t('page.reports.sales.headsets'),
              key: 'headsets',
              field: 'headsets',
            },
            {
              title: t('page.reports.sales.revenuePerHeadset'),
              field: 'projectedRevenue',
              key: 'revenuePerHeadset',
            },
            {
              title: t('page.reports.sales.netRevenuePerHeadset'),
              field: 'projectedRevenue',
              key: 'netRevenuePerHeadset',
              render: (value, record) =>
                `${(
                  record.projectedRevenue *
                  (1 - getCompanyCommission(record.companyId) / 100)
                ).toFixed(0)}`,
              summaryRender: (value, record) =>
                `${record.totalProjectedRevenueWithCommission.toFixed(0)}`,
            },
            {
              title: t('page.reports.sales.total'),
              field: 'totalSucceeded',
              key: 'total',
            },
            {
              title: t('page.reports.sales.netTotal'),
              field: 'totalSucceeded',
              key: 'netTotal',
              render: (value, record) =>
                `${(
                  record.totalSucceeded *
                  (1 - getCompanyCommission(record.companyId) / 100)
                ).toFixed(0)}`,
              summaryRender: (value, record) =>
                `${record.totalSucceededWithCommission.toFixed(0)}`,
            },
            {
              title: t('page.reports.sales.reception'),
              field: 'deskSucceeded',
              key: 'reception',
            },
            {
              title: t('page.reports.sales.room'),
              field: 'roomBillSucceeded',
              key: 'room',
            },
          ] as XlsxTableConfig<BillingRecord>[])),
    ],
    [account?.role, getCompanyCommission, t],
  );

  const config: StickyTableConfig<BillingRecord>[] = useMemo(
    () => [
      {
        title: t('page.reports.sales.company'),
        field: 'name',
        key: 'company',
        sorter: (a, b, sortOrder) =>
          compareString(a.name, b.name) * (sortOrder === 'descend' ? -1 : 1),
        render: (value, record) => {
          return (
            <Link
              className={cl(
                styles.company,
                !accessMap[HOTELS].length && styles.inactive,
              )}
              src={
                accessMap[HOTELS].length
                  ? `/${HOTELS}/${record.companyId}/${accessMap[HOTELS][0]}`
                  : null
              }
            >
              <div>
                <span className={styles.title}>{value}</span>
                {account?.role !== RoleEnum.airpano &&
                  record.todayPaymentCount > 0 && (
                    <span className={styles.count}>
                      <Crown /> <span>{record.todayPaymentCount}</span>
                    </span>
                  )}
              </div>
              {account?.role !== RoleEnum.airpano && (
                <div className={styles.secondary}>
                  {record.activatedAt
                    ? formatDate(record.activatedAt)
                    : t('general.inactive')}
                  , {getCompanyCommission(record.companyId)} %
                </div>
              )}
            </Link>
          );
        },
        summaryRender: (value, record) => (
          <div>
            <span>{value}</span>
            {account?.role !== RoleEnum.airpano &&
              record.todayPaymentCount > 0 && (
                <span className={styles.summaryCount}>
                  <Crown /> <span>{record.todayPaymentCount}</span>
                </span>
              )}
          </div>
        ),
      },
      ...(account?.role === RoleEnum.airpano
        ? ([
            {
              title: t('page.reports.sales.headsets'),
              key: 'headsets',
              field: 'headsets',
              sorter: (a, b, sortOrder) =>
                (a.headsets - b.headsets) * (sortOrder === 'descend' ? -1 : 1),
            },
            {
              title: t('page.reports.sales.headsetsWithoutSessions'),
              field: 'headsetsWithoutSessions',
              key: 'headsetsWithoutSessions',
              sorter: (a, b, sortOrder) =>
                (a.headsetsWithoutSessions - b.headsetsWithoutSessions) *
                (sortOrder === 'descend' ? -1 : 1),
            },
            {
              title: t('page.reports.sales.conversion'),
              key: 'conversion',
              field: 'conversion',
              sorter: (a, b, sortOrder) =>
                (a.conversion - b.conversion) *
                (sortOrder === 'descend' ? -1 : 1),
              render: (value) => ((value ?? 0) as number).toFixed(2),
            },
            {
              title: t('page.reports.sales.sessions'),
              key: 'sessions',
              field: 'sessions',
              sorter: (a, b, sortOrder) =>
                (a.sessions - b.sessions) * (sortOrder === 'descend' ? -1 : 1),
            },
            {
              title: t('page.reports.sales.paymentsCount'),
              field: 'totalSucceededCount',
              key: 'paymentsCount',
              sorter: (a, b, sortOrder) =>
                (a.totalSucceededCount - b.totalSucceededCount) *
                (sortOrder === 'descend' ? -1 : 1),
            },
          ] as StickyTableConfig<BillingRecord>[])
        : ([
            {
              title: t('page.reports.sales.paymentsCount'),
              field: 'totalSucceededCount',
              key: 'paymentsCount',
              sorter: (a, b, sortOrder) =>
                (a.totalSucceededCount - b.totalSucceededCount) *
                (sortOrder === 'descend' ? -1 : 1),
            },
            {
              title: t('page.reports.sales.sessions'),
              key: 'sessions',
              field: 'sessions',
              sorter: (a, b, sortOrder) =>
                (a.sessions - b.sessions) * (sortOrder === 'descend' ? -1 : 1),
            },
            {
              title: t('page.reports.sales.conversion'),
              key: 'conversion',
              field: 'conversion',
              sorter: (a, b, sortOrder) =>
                (a.conversion - b.conversion) *
                (sortOrder === 'descend' ? -1 : 1),
              render: (value) => ((value ?? 0) as number).toFixed(2),
            },
            {
              title: t('page.reports.sales.headsets'),
              key: 'headsets',
              field: 'headsets',
              sorter: (a, b, sortOrder) =>
                (a.headsets - b.headsets) * (sortOrder === 'descend' ? -1 : 1),
            },
            {
              title: t('page.reports.sales.revenuePerHeadset'),
              field: 'projectedRevenue',
              key: 'revenuePerHeadset',
              sorter: (a, b, sortOrder) =>
                (a.projectedRevenue - b.projectedRevenue) *
                (sortOrder === 'descend' ? -1 : 1),
              render: (value, record) =>
                `${value} ${currencySign(record.currency)}`,
            },
            {
              title: t('page.reports.sales.netRevenuePerHeadset'),
              field: 'projectedRevenue',
              key: 'netRevenuePerHeadset',
              sorter: (a, b, sortOrder) =>
                (a.projectedRevenue - b.projectedRevenue) *
                (sortOrder === 'descend' ? -1 : 1),
              render: (value, record) =>
                `${(
                  record.projectedRevenue *
                  (1 - getCompanyCommission(record.companyId) / 100)
                ).toFixed(0)} ${currencySign(record.currency)}`,
              summaryRender: (value, record) =>
                `${record.totalProjectedRevenueWithCommission.toFixed(0)} ${currencySign(record.currency)}`,
            },
            {
              title: t('page.reports.sales.total'),
              field: 'totalSucceeded',
              key: 'total',
              sorter: (a, b, sortOrder) =>
                (a.totalSucceeded - b.totalSucceeded) *
                (sortOrder === 'descend' ? -1 : 1),
              render: (value, record) =>
                `${value} ${currencySign(record.currency)}`,
            },
            {
              title: t('page.reports.sales.netTotal'),
              field: 'totalSucceeded',
              key: 'netTotal',
              sorter: (a, b, sortOrder) =>
                (a.totalSucceeded - b.totalSucceeded) *
                (sortOrder === 'descend' ? -1 : 1),
              render: (value, record) =>
                `${(
                  record.totalSucceeded *
                  (1 - getCompanyCommission(record.companyId) / 100)
                ).toFixed(0)} ${currencySign(record.currency)}`,
              summaryRender: (value, record) =>
                `${record.totalSucceededWithCommission.toFixed(0)} ${currencySign(record.currency)}`,
            },
            {
              title: t('page.reports.sales.reception'),
              field: 'deskSucceeded',
              key: 'reception',
              sorter: (a, b, sortOrder) =>
                (a.deskSucceeded - b.deskSucceeded) *
                (sortOrder === 'descend' ? -1 : 1),
              render: (value, record) =>
                `${value} ${currencySign(record.currency)}`,
            },
            {
              title: t('page.reports.sales.room'),
              field: 'roomBillSucceeded',
              key: 'room',
              sorter: (a, b, sortOrder) =>
                (a.roomBillSucceeded - b.roomBillSucceeded) *
                (sortOrder === 'descend' ? -1 : 1),
              render: (value, record) =>
                `${value} ${currencySign(record.currency)}`,
            },
          ] as StickyTableConfig<BillingRecord>[])),
    ],
    [account?.role, getCompanyCommission, t, accessMap],
  );

  const {
    stickyCols,
    stickyColsFilter,
    modalStickyColsFilter,
    applySCChanges,
    resetSCChanges,
  } = useStickyCols({
    config,
    className: styles.stickyCols,
    modalClassName: styles.modalStickyCols,
  });

  const getEmptySales = useCallback(
    (records: BillingRecord[]) =>
      records.length > 0
        ? account?.companies
            .filter((c) => !records.map((b) => b.companyId).includes(c.id))
            .map((c) => ({
              name: c.name,
              companyId: c.id,
              activatedAt: c.activatedAt,
              currency: currencySign('rub'),
              headsets: c.headsets,
              avgCompanyCheckins: 80,
              avgCompanyDevices: c.headsets,
              totalSucceeded: 0,
              totalPending: 0,
              totalCanceled: 0,
              totalSucceededCount: 0,
              sessions: 0,
              conversion: 0,
              headsetsWithoutSessions: 0,
              totalPendingCount: 0,
              totalCanceledCount: 0,
              totalPaymentSystem: 0,
              totalReception: 0,
              totalRoomBill: 0,
              totalRoomBillAccess: 0,
              todayPaymentCount: 0,
              totalProjectedRevenue: 0,
              projectedRevenue: 0,
              deskSucceeded: 0,
              roomBillSucceeded: 0,
              totalSucceededWithCommission: 0,
              totalProjectedRevenueWithCommission: 0,
            })) || []
        : [],
    [account?.companies],
  );

  const filteredRecords = useMemo(() => {
    const records = billingRecords.map((record) => ({
      ...record,
      conversion: record.sessions
        ? record.totalSucceededCount / record.sessions
        : 0,
      headsetsWithoutSessions: record.headsetsWithoutSessions ?? 0,
    }));

    if (searchString.length) {
      return [...records, ...getEmptySales(records)].filter((c) =>
        c.name.toLowerCase().startsWith(searchString.toLowerCase()),
      );
    }

    return [...records, ...getEmptySales(records)];
  }, [billingRecords, getEmptySales, searchString]);

  const getSummaryRecord = useCallback(
    (records: BillingRecord[]) => {
      let totalSucceeded = 0;
      let totalSucceededWithCommission = 0;
      let totalPending = 0;
      let totalCanceled = 0;
      let totalSucceededCount = 0;
      let totalPendingCount = 0;
      let totalCanceledCount = 0;
      let totalReception = 0;
      let totalRoomBill = 0;
      let totalHeadsets = 0;
      let todayPaymentCount = 0;
      let totalProjectedRevenue = 0;
      let totalProjectedRevenueWithCommission = 0;
      let totalAvgCompanyDevices = 0;
      let totalAvgCompanyCheckins = 0;
      let sessions = 0;
      let totalHeadsetsWithoutSessions = 0;

      records.forEach((record) => {
        todayPaymentCount += record.todayPaymentCount;
        totalSucceededCount += record.totalSucceededCount;
        sessions += record.sessions;
        totalHeadsets += record.headsets;
        totalHeadsetsWithoutSessions += record.headsetsWithoutSessions ?? 0;

        if (account?.role !== RoleEnum.airpano) {
          totalSucceeded += record.totalSucceeded;
          totalPending += record.totalPending;
          totalCanceled += record.totalCanceled;
          totalPendingCount += record.totalPendingCount;
          totalCanceledCount += record.totalCanceledCount;
          totalReception += record.deskSucceeded;
          totalRoomBill += record.roomBillSucceeded;
          totalProjectedRevenue += record.projectedRevenue * record.headsets;
          totalAvgCompanyDevices += record.avgCompanyDevices;
          totalAvgCompanyCheckins += record.avgCompanyCheckins;
          totalSucceededWithCommission +=
            record.totalSucceeded *
            (1 - getCompanyCommission(record.companyId) / 100);
          totalProjectedRevenueWithCommission +=
            record.projectedRevenue *
            record.headsets *
            (1 - getCompanyCommission(record.companyId) / 100);
        }
      });

      let projectedRevenue = 0;
      let avgCompanyCheckins = 0;
      let totalProjectedRevenueWithCommissionCalc = 0;

      if (account?.role !== RoleEnum.airpano) {
        projectedRevenue =
          Math.round(totalProjectedRevenue / totalHeadsets) || 0;
        avgCompanyCheckins = Math.round(
          totalAvgCompanyCheckins / records.length,
        );
        totalProjectedRevenueWithCommissionCalc = Math.round(
          totalProjectedRevenueWithCommission / totalHeadsets,
        );
      }

      return {
        companyId: '-1',
        activatedAt: '-1',
        currency: currencySign('rub'),
        name: t('general.total'),
        headsets: totalHeadsets,
        headsetsWithoutSessions: totalHeadsetsWithoutSessions,
        todayPaymentCount: todayPaymentCount,
        totalSucceeded: totalSucceeded,
        totalCanceled: totalCanceled,
        totalPending: totalPending,
        totalSucceededCount: totalSucceededCount,
        sessions: sessions,
        conversion: sessions ? totalSucceededCount / sessions : 0,
        totalCanceledCount: totalCanceledCount,
        totalPendingCount: totalPendingCount,
        deskSucceeded: totalReception,
        roomBillSucceeded: totalRoomBill,
        projectedRevenue,
        avgCompanyCheckins,
        avgCompanyDevices: totalAvgCompanyDevices,
        totalSucceededWithCommission: totalSucceededWithCommission,
        totalProjectedRevenueWithCommission:
          totalProjectedRevenueWithCommissionCalc,
      };
    },
    [account?.role, getCompanyCommission, t],
  );

  const summaryRecord: BillingRecord = useMemo(
    () => getSummaryRecord(filteredRecords),
    [getSummaryRecord, filteredRecords],
  );

  useEffect(() => {
    if (!exportData || exportData.uuid !== exportReqUuid) {
      return;
    }

    setExportReqUuid(null);

    const data = [...exportData.records, ...getEmptySales(exportData.records)];

    writeToXlsx(
      {
        config: xlsxConfig,
        data,
        summaryRecord: getSummaryRecord(data),
      },
      exportData.filename,
    );
  }, [
    exportData,
    exportReqUuid,
    getEmptySales,
    getSummaryRecord,
    writeToXlsx,
    xlsxConfig,
  ]);

  const isRangeChanged = useMemo(
    () =>
      range.start?.time !== initRange.start?.time ||
      range.end?.time !== initRange.end?.time,
    [
      initRange.start?.time,
      initRange.end?.time,
      range.start?.time,
      range.end?.time,
    ],
  );

  useEffect(() => {
    let start: string | undefined = undefined;
    let end: string | undefined = undefined;

    if (range.start && range.end) {
      start = range.start.format;
      end = range.end.format;
    }

    setIsLoading(true);

    dispatch(
      fetchBillingAction({
        params: {
          start,
          end,
        },
      }),
    );
  }, [dispatch, range]);

  useEffect(() => {
    setIsLoading(state.isPending);
  }, [state.isPending]);

  useEffect(() => {
    setIsFiltered(Boolean(searchString) || isRangeChanged);
  }, [searchString, isRangeChanged]);

  const resetFilter = useCallback(() => {
    if (isRangeChanged) {
      setIsLoading(true);
    }

    resetDRPToInit();
    clearSearch();
    setIsFiltered(false);
  }, [clearSearch, resetDRPToInit, isRangeChanged]);

  const blocker = useCallback<BlockerFunction>(
    ({ nextLocation: { pathname } }) => {
      setBackUrl(
        pathname.startsWith(`/${HOTELS}/`) ? `/${REPORTS}/${SALES}` : null,
      );

      return false;
    },
    [setBackUrl],
  );

  useBlocker(blocker);

  useEffect(
    () => () => {
      dispatch(resetBillingState());
    },
    [dispatch],
  );

  return (
    <div className={styles.container}>
      <FilterModal
        show={showSearchModal}
        close={() => {
          setShowSearchModal(false);
        }}
        onApply={() => {
          applySearch();
          setShowSearchModal(false);
        }}
        onCancel={() => {
          cancelSearch();
          setShowSearchModal(false);
        }}
        isSearch
      >
        {modalSearchInput}
      </FilterModal>
      <FilterModal
        show={showFiltersModal}
        close={() => {
          setShowFiltersModal(false);
        }}
        onApply={() => {
          applyDRPChanges();
          applySCChanges();
          setShowFiltersModal(false);
        }}
        onCancel={() => {
          resetSCChanges();
          resetDRPChanges();
        }}
      >
        {modalDateRangePicker}
        {modalStickyColsFilter}
      </FilterModal>
      <ExportModal
        show={Boolean(exportReqUuid)}
        close={() => {
          setExportReqUuid(null);
        }}
      />
      <div
        className={styles.topSection}
        style={{
          minWidth: headerWidth,
          maxWidth: headerWidth,
        }}
      >
        <div
          className={cl(
            styles.pageSwitcherContainer,
            accessMap[REPORTS].length === 1 && styles.inactive,
          )}
        >
          <PageSwitcher
            className={styles.pageSwitcher}
            selectClassName={styles.mobileSelect}
          />
          <div className={styles.filterButtons}>
            <FilterModalButton
              setShow={setShowSearchModal}
              icon="search"
              badge={Boolean(searchString)}
              disabled={isLoading}
            />
            <FilterModalButton
              setShow={setShowFiltersModal}
              badge={Boolean(rangeIndicatorText)}
              disabled={isLoading}
            />
            <ExportSelect
              onExport={(mode) => {
                if (mode === 'selected') {
                  writeToXlsx(
                    {
                      config: xlsxConfig,
                      data: filteredRecords,
                      summaryRecord,
                    },
                    xlsxFileName,
                  );
                } else {
                  fetchExportBilling(mode);
                }
              }}
              className={styles.filterIcon}
              isDisabled={isLoading}
              small
            />
            <FilterModalIndicator
              icon="search"
              text={searchString}
              clear={clearSearch}
              disabled={isLoading}
            />
            <FilterModalIndicator
              icon="calendar"
              text={rangeIndicatorText}
              clear={clearDRP}
              disabled={isLoading}
            />
          </div>
        </div>
        <div className={styles.filtersWrapper}>
          <div className={styles.filters}>
            {dateRangePicker}
            {searchInput}
            {stickyColsFilter}
          </div>
          <div className={styles.buttons}>
            <ExportSelect
              onExport={(mode) => {
                if (mode === 'selected') {
                  writeToXlsx(
                    {
                      config: xlsxConfig,
                      data: filteredRecords,
                      summaryRecord,
                    },
                    xlsxFileName,
                  );
                } else {
                  fetchExportBilling(mode);
                }
              }}
              className={styles.button}
              isDisabled={isLoading}
            />
          </div>
        </div>
      </div>
      <StickyTable
        config={account?.role ? config : null}
        data={filteredRecords}
        summaryRecord={summaryRecord}
        withPaginator
        isLoading={isLoading}
        stickyCols={stickyCols}
        className={styles.table}
        isFiltered={isFiltered}
        resetFilter={resetFilter}
        notFoundText={t('general.resetFilters')}
      />
    </div>
  );
};

export default ReportsSales;
